
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface FilterHeaderProps {
  selectedRegion: string;
  selectedCountry: string;
  selectedAccountName: string;
  selectedProductLine: string;
  selectedIndustry: string;
  filterType: "product" | "industry";
  countryOptions: string[];
  accountNameOptions: string[];
  regions: string[];
  productLines: string[];
  industries: string[];
  handleRegionChange: (value: string) => void;
  handleCountryChange: (value: string) => void;
  handleAccountNameChange: (value: string) => void;
  handleProductLineChange: (value: string) => void;
  handleIndustryChange: (value: string) => void;
  handleFilterTypeChange: (value: "product" | "industry") => void;
}

const FilterHeader: React.FC<FilterHeaderProps> = ({
  selectedRegion,
  selectedCountry,
  selectedAccountName,
  selectedProductLine,
  selectedIndustry,
  filterType,
  countryOptions,
  accountNameOptions,
  regions,
  productLines,
  industries,
  handleRegionChange,
  handleCountryChange,
  handleAccountNameChange,
  handleProductLineChange,
  handleIndustryChange,
  handleFilterTypeChange
}) => {
  return (
    <Card className="bg-gradient-to-r from-[#1e293b] to-[#1a1f2c] border-none shadow-lg mb-4">
      <CardContent className="p-4 py-[4px] my-[10px] bg-secondary border-none shadow-none">
        <div className="flex justify-between items-center mb-0 bg-inherit">
          <h3 className="text-white text-lg font-semibold">Gap Analysis & Revenue Bridge</h3>
          
          <div className="flex items-center gap-2 bg-inherit">
            <div className="flex items-center gap-1">
              <span className="whitespace-nowrap font-medium text-primary-light">Filter by:</span>
            </div>
            
            <Select value={selectedRegion} onValueChange={handleRegionChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#6366F1]/30">
                <SelectItem value="All">All</SelectItem>
                {regions.slice(1).map(region => <SelectItem key={region} value={region}>{region}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <Select value={selectedCountry} onValueChange={handleCountryChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#EC4899]/30">
                <SelectItem value="All">All</SelectItem>
                {countryOptions.slice(1).map(country => <SelectItem key={country} value={country}>{country}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <Select value={selectedAccountName} onValueChange={handleAccountNameChange}>
              <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black border border-[#10B981]/30">
                <SelectItem value="All">All</SelectItem>
                {accountNameOptions.slice(1).map(accountName => <SelectItem key={accountName} value={accountName}>{accountName}</SelectItem>)}
              </SelectContent>
            </Select>
            
            <RadioGroup defaultValue="product" className="flex flex-col items-start space-y-0 text-[10px]" value={filterType} onValueChange={value => handleFilterTypeChange(value as "product" | "industry")}>
              <div className="flex items-center space-x-1">
                <RadioGroupItem className="h-4 w-4" id="product" value="product" />
                <Label htmlFor="product" className="cursor-pointer text-white space-y-0 text-[10px]">Product</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem className="h-4 w-4" id="industry" value="industry" />
                <Label htmlFor="industry" className="cursor-pointer text-white space-y-0 text-[10px]">Industry</Label>
              </div>
            </RadioGroup>
            
            {filterType === "product" ? (
              <Select value={selectedProductLine} onValueChange={handleProductLineChange}>
                <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black border border-[#F59E0B]/30">
                  <SelectItem value="All">All</SelectItem>
                  {productLines.slice(1).map(product => <SelectItem key={product} value={product}>{product}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Select value={selectedIndustry} onValueChange={handleIndustryChange}>
                <SelectTrigger className="w-auto min-w-[120px] border-primary/30 text-white bg-secondary-light/5">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black border border-[#F59E0B]/30">
                  <SelectItem value="All">All</SelectItem>
                  {industries.slice(1).map(industry => <SelectItem key={industry} value={industry}>{industry}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterHeader;
